import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { bool, func, number, shape, string } from 'prop-types';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { alpha } from '@material-ui/core/styles/colorManipulator';

// Aire entre el recorte del spotlight y el elemento resaltado.
const PAD = 6;
// Ancho fijo del globo: con ancho variable el texto salta entre pasos.
const TIP_WIDTH = 330;
// Separación entre el globo y el elemento.
const GAP = 14;
// Margen mínimo contra el borde de la ventana.
const EDGE = 12;

// Dos medidas son la misma si el elemento no se movió: evita re-render por cada
// medición del sondeo.
const sameRect = (a, b) => {
  if (!a || !b) {
    return a === b;
  }
  return a.top === b.top && a.left === b.left
    && a.width === b.width && a.height === b.height;
};

/**
 * Bandas que rodean el recorte (arriba, abajo, izquierda, derecha).
 *
 * El bloqueo de clics no puede ser una sola capa de pantalla completa: dejaría
 * muerto también al elemento del paso, y varios pasos invitan a usarlo ("toca
 * su fila", "la flecha te regresa al menú"). Con cuatro bandas, el hueco del
 * recorte queda libre y el resto de la pantalla, bloqueado.
 */
const blockerBands = (rect) => {
  const top = Math.max(0, rect.top - PAD);
  const bottom = Math.max(0, rect.bottom + PAD);
  const left = Math.max(0, rect.left - PAD);
  const right = Math.max(0, rect.right + PAD);
  const height = Math.max(0, bottom - top);

  return [
    {
      height: top,
      left: 0,
      right: 0,
      top: 0,
    },
    {
      bottom: 0,
      left: 0,
      right: 0,
      top: bottom,
    },
    {
      height,
      left: 0,
      top,
      width: left,
    },
    {
      height,
      left: right,
      right: 0,
      top,
    },
  ];
};

// Coloca el globo del lado pedido y lo voltea si se saldría de la ventana.
const placeTip = (rect, placement, tipHeight) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!rect) {
    return {
      left: Math.max(EDGE, (vw - TIP_WIDTH) / 2),
      top: Math.max(EDGE, (vh - tipHeight) / 2),
    };
  }

  const clampX = (x) => Math.min(Math.max(EDGE, x), vw - TIP_WIDTH - EDGE);
  const clampY = (y) => Math.min(Math.max(EDGE, y), vh - tipHeight - EDGE);

  const fits = {
    bottom: rect.bottom + GAP + tipHeight < vh - EDGE,
    left: rect.left - GAP - TIP_WIDTH > EDGE,
    right: rect.right + GAP + TIP_WIDTH < vw - EDGE,
    top: rect.top - GAP - tipHeight > EDGE,
  };

  // Si el lado pedido no cabe, se usa el primero que sí.
  const order = [placement, 'bottom', 'right', 'left', 'top'];
  const side = order.find((option) => fits[option]) || 'bottom';

  if (side === 'right') {
    return {
      left: rect.right + GAP,
      top: clampY(rect.top + rect.height / 2 - tipHeight / 2),
    };
  }
  if (side === 'left') {
    return {
      left: rect.left - GAP - TIP_WIDTH,
      top: clampY(rect.top + rect.height / 2 - tipHeight / 2),
    };
  }
  if (side === 'top') {
    return {
      left: clampX(rect.left + rect.width / 2 - TIP_WIDTH / 2),
      top: rect.top - GAP - tipHeight,
    };
  }
  return {
    left: clampX(rect.left + rect.width / 2 - TIP_WIDTH / 2),
    top: rect.bottom + GAP,
  };
};

/**
 * Overlay del tour guiado: oscurece la pantalla, recorta el elemento del paso
 * actual y muestra el globo con su explicación.
 *
 * Los pasos apuntan a sus elementos por selector CSS (`data-tour="..."`), no
 * por ref: así una pantalla se instrumenta agregando un atributo, sin cablear
 * props a través de los componentes.
 *
 * Se dibuja en un portal a `document.body` porque `position: fixed` se mide
 * contra el ancestro si alguno tiene `transform`, y las tarjetas del panel lo
 * usan.
 */
function Tour({ active, back, index, next, skip, step, total }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const [rect, setRect] = useState(null);
  // Alto real del globo: se mide para centrarlo y para decidir si cabe.
  const [tipHeight, setTipHeight] = useState(180);
  const [tipNode, setTipNode] = useState(null);

  // Medición del objetivo: al entrar al paso, y de nuevo si la ventana cambia.
  useEffect(() => {
    if (!active || !step) {
      return undefined;
    }

    const measure = () => {
      const element = step.target ? document.querySelector(step.target) : null;
      const next = element ? element.getBoundingClientRect() : null;
      setRect((prev) => (sameRect(prev, next) ? prev : next));
    };

    const element = step.target ? document.querySelector(step.target) : null;
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    measure();
    // El scroll suave tarda: se vuelve a medir cuando terminó.
    const timer = setTimeout(measure, 340);
    // El paso puede invitar a usar el elemento resaltado, y usarlo mueve la
    // pantalla (o lo hace desaparecer, como la flecha de regreso al salir de la
    // pantalla): el sondeo mantiene el recorte pegado a donde esté.
    const poll = setInterval(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);

    return () => {
      clearTimeout(timer);
      clearInterval(poll);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, step]);

  useEffect(() => {
    if (tipNode) {
      setTipHeight(tipNode.offsetHeight);
    }
  }, [tipNode, step]);

  // Salir con Escape, avanzar y retroceder con las flechas.
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        skip();
      } else if (event.key === 'ArrowRight') {
        next();
      } else if (event.key === 'ArrowLeft') {
        back();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, back, next, skip]);

  if (!active || !step) {
    return null;
  }

  const last = index + 1 >= total;
  const position = placeTip(rect, step.placement, tipHeight);

  return createPortal(
    <Box>
      {/* Captura los clics de todo MENOS el elemento del paso: con el recorte
          libre, lo que el globo explica se puede probar en el momento. */}
      {rect
        ? blockerBands(rect).map((band, index) => (
          <Box
            key={index}
            style={{
              position: 'fixed',
              zIndex: theme.zIndex.modal,
              ...band,
            }}
            onClick={(event) => event.stopPropagation()}
          />
        ))
        : (
          <Box
            style={{
              bottom: 0,
              left: 0,
              position: 'fixed',
              right: 0,
              top: 0,
              zIndex: theme.zIndex.modal,
            }}
            onClick={(event) => event.stopPropagation()}
          />
        )
      }

      {/* Recorte: la sombra gigante oscurece todo menos este rectángulo */}
      {rect &&
        <Box style={{
          borderRadius: 12,
          boxShadow: `0 0 0 9999px ${alpha('#0f141a', 0.55)}`,
          height: rect.height + PAD * 2,
          left: rect.left - PAD,
          outline: `2px solid ${primary}`,
          pointerEvents: 'none',
          position: 'fixed',
          top: rect.top - PAD,
          transition: 'top 180ms ease, left 180ms ease, width 180ms ease, height 180ms ease',
          width: rect.width + PAD * 2,
          zIndex: theme.zIndex.modal,
        }} />
      }

      {/* Sin objetivo visible se oscurece todo y el globo va al centro */}
      {!rect &&
        <Box style={{
          backgroundColor: alpha('#0f141a', 0.55),
          bottom: 0,
          left: 0,
          pointerEvents: 'none',
          position: 'fixed',
          right: 0,
          top: 0,
          zIndex: theme.zIndex.modal,
        }} />
      }

      <Paper
        elevation={8}
        ref={setTipNode}
        style={{
          borderRadius: 14,
          left: position.left,
          padding: 20,
          position: 'fixed',
          top: position.top,
          width: TIP_WIDTH,
          zIndex: theme.zIndex.modal + 1,
        }}
      >
        <Typography variant="overline" color="textSecondary">
          Paso {index + 1} de {total}
        </Typography>
        <Typography variant="subtitle1" style={{
          fontWeight: 700,
          marginTop: 2,
        }}>
          {step.title}
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{
          marginTop: 6,
        }}>
          {step.body}
        </Typography>

        {/* Progreso */}
        <Box style={{
          display: 'flex',
          marginTop: 16,
        }}>
          {Array.from({
            length: total,
          }).map((_, dot) => (
            <Box
              key={dot}
              style={{
                backgroundColor: dot === index ? primary : alpha(primary, 0.2),
                borderRadius: 3,
                flex: 1,
                height: 4,
                marginRight: dot === total - 1 ? 0 : 4,
              }}
            />
          ))}
        </Box>

        <Box style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 14,
        }}>
          <Button size="small" onClick={skip}>
            {last ? 'Cerrar' : 'Saltar'}
          </Button>
          <Box>
            {index > 0 &&
              <Button size="small" style={{
                marginRight: 8,
              }} onClick={back}>
                Atrás
              </Button>
            }
            <Button
              color="primary"
              disableElevation
              size="small"
              variant="contained"
              onClick={next}
            >
              {last ? 'Entendido' : 'Siguiente'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>,
    document.body,
  );
}

Tour.propTypes = {
  active: bool,
  back: func.isRequired,
  index: number,
  next: func.isRequired,
  skip: func.isRequired,
  step: shape({
    body: string,
    placement: string,
    target: string,
    title: string,
  }),
  total: number,
};

export default Tour;
